import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/axiosConfig';
import * as XLSX from 'xlsx';
import ReportAnalytics from './ReportAnalytics';

const RaporArsiv = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('arsiv');
  
  // Arşiv sekmesi state'leri
  const [transfers, setTransfers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    agencyId: '',
    pickupLocation: '',
    dropoffLocation: '',
    serviceType: '',
    status: '',
    driverName: ''
  });
  const pageSize = 20;

  // Aylık rapor modal state
  const [showMonthlyReportModal, setShowMonthlyReportModal] = useState(false);
  const [monthlyReportData, setMonthlyReportData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    agencyId: ''
  });
  const [monthlyReportLoading, setMonthlyReportLoading] = useState(false);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      // Tüm kayıtları almak için size parametresini büyük gönderiyoruz
      const response = await api.get('/api/admin/transfers', {
        params: {
          size: 10000  // Tüm arşiv kayıtlarını almak için
        }
      });
      
      console.log('🔍 API Response:', response);
      console.log('🔍 Response Data:', response.data);
      
      const data = response.data.transfers || response.data || [];
      console.log('🔍 Parsed Data:', data);
      console.log('🔍 Data Length:', data.length);
      
      const sortedData = data.sort((a, b) => {
        const dateA = new Date(`${a.pickup_date}T${a.pickup_time}`);
        const dateB = new Date(`${b.pickup_date}T${b.pickup_time}`);
        return dateB - dateA;
      });
      
      console.log('🔍 Sorted Data Length:', sortedData.length);
      setTransfers(sortedData);
    } catch (error) {
      console.error('❌ Transfer kayıtları yüklenemedi:', error);
      console.error('❌ Error Response:', error.response);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAgencies = async () => {
    try {
      const response = await api.get('/api/admin/agencies');
      setAgencies(response.data.agencies || response.data || []);
    } catch (error) {
      console.error('Acenteler yüklenemedi:', error);
      setAgencies([]);
    }
  };

  useEffect(() => {
    loadTransfers();
    loadAgencies();
    
    const interval = setInterval(() => {
      loadTransfers();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const formatDateTime = (date, time) => {
    if (!date || !time) return '-';
    try {
      const dateObj = new Date(`${date}T${time}`);
      return dateObj.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getServiceTypeLabel = (type) => {
    const types = {
      'PATIENT': 'Hasta',
      'STAFF': 'Personel', 
      'EXECUTIVE': 'Yönetici',
      'DOCUMENT': 'Evrak'
    };
    return types[type] || type || '-';
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'RECEIVED': 'Alındı',
      'PLANNED': 'Planlandı',
      'ENROUTE': 'Yolda',
      'PICKED_UP': 'Yolcu Alındı',
      'COMPLETED': 'Tamamlandı'
    };
    return statuses[status] || status || '-';
  };

  const getStatusColor = (status) => {
    const colors = {
      'RECEIVED': 'bg-yellow-100 text-yellow-800',
      'PLANNED': 'bg-blue-100 text-blue-800',
      'ENROUTE': 'bg-orange-100 text-orange-800',
      'PICKED_UP': 'bg-purple-100 text-purple-800',
      'COMPLETED': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Filtreleme
  const filteredTransfers = useMemo(() => {
    return transfers.filter(transfer => {
      if (filters.startDate && transfer.pickup_date < filters.startDate) return false;
      if (filters.endDate && transfer.pickup_date > filters.endDate) return false;
      if (filters.agencyId && transfer.agency_id !== parseInt(filters.agencyId)) return false;
      if (filters.pickupLocation && !transfer.pickup_addr?.toLowerCase().includes(filters.pickupLocation.toLowerCase())) return false;
      if (filters.dropoffLocation && !transfer.dropoff_addr?.toLowerCase().includes(filters.dropoffLocation.toLowerCase())) return false;
      if (filters.serviceType && transfer.service_type !== filters.serviceType) return false;
      if (filters.status && transfer.status !== filters.status) return false;
      if (filters.driverName && !transfer.driver_name?.toLowerCase().includes(filters.driverName.toLowerCase())) return false;
      return true;
    });
  }, [transfers, filters]);

  // Sayfalama
  const totalPages = Math.max(1, Math.ceil(filteredTransfers.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTransfers.slice(start, start + pageSize);
  }, [filteredTransfers, page]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  // Excel Export
  const exportToExcel = () => {
    const dataToExport = filteredTransfers.map((transfer, index) => ({
      'Sıra No': index + 1,
      'Acente': transfer.agency_name || 'BHT Clinic Admin',
      'Alış Adresi': transfer.pickup_addr || '',
      'Bırakış Adresi': transfer.dropoff_addr || '',
      'Tarih-Saat': formatDateTime(transfer.pickup_date, transfer.pickup_time),
      'Yolcu Sayısı': transfer.pax_count || 1,
      'Yolcu Adı': transfer.passenger_name || '-',
      'TC/Pasaport': transfer.passenger_id_no || '-',
      'Hizmet Tipi': getServiceTypeLabel(transfer.service_type),
      'Notlar': transfer.notes || '-',
      'Durum': getStatusLabel(transfer.status),
      'Şoför': transfer.driver_name || '-',
      'Oluşturulma': new Date(transfer.created_at).toLocaleDateString('tr-TR')
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transfer Kayıtları');
    
    const now = new Date();
    const fileName = `BHT_Transfer_Kayitlari_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Aylık rapor oluşturma
  const generateMonthlyReport = async () => {
    setMonthlyReportLoading(true);
    
    try {
      const response = await api.post('/api/admin/reports/monthly', {
        year: monthlyReportData.year,
        month: monthlyReportData.month,
        agency_id: monthlyReportData.agencyId || null
      });
      
      if (response.data.success && response.data.data) {
        generateExcelReport(response.data.data);
      } else {
        alert(response.data.message || 'Seçilen dönemde transfer bulunamadı.');
      }
      
      setShowMonthlyReportModal(false);
      
    } catch (error) {
      console.error('Aylık rapor oluşturulurken hata:', error);
      alert('Aylık rapor oluşturulurken bir hata oluştu: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMonthlyReportLoading(false);
    }
  };

  // Excel raporu oluşturma
  const generateExcelReport = (reportData) => {
    const wb = XLSX.utils.book_new();
    
    // 1. ÖZET SAYFASI
    const summaryData = [
      ['BHT TRANSFER SİSTEMİ - AYLIK RAPOR', '', '', ''],
      ['', '', '', ''],
      ['Rapor Dönemi:', `${reportData.period.month_name} ${reportData.period.year}`, '', ''],
      ['Rapor Tarihi:', new Date().toLocaleDateString('tr-TR'), '', ''],
      ['', '', '', ''],
      ['GENEL İSTATİSTİKLER', '', '', ''],
      ['Toplam Transfer:', reportData.summary.total_transfers, '', ''],
      ['Toplam Yolcu:', reportData.summary.total_passengers, '', ''],
      ['Ortalama Yolcu/Transfer:', reportData.summary.avg_passengers_per_transfer, '', ''],
      ['', '', '', ''],
      ['HAVALIMANI İŞLERİ', '', '', ''],
      ['Havalimanı Transferleri:', reportData.summary.airport_transfers, '', ''],
      ['Havalimanı Oranı (%):', reportData.summary.airport_ratio, '', ''],
      ['', '', '', ''],
      ['SERVİS TİPİ DAĞILIMI', '', '', ''],
      ...Object.entries(reportData.summary.service_distribution).map(([type, count]) => [
        getServiceTypeLabel(type), count, `${((count / reportData.summary.total_transfers) * 100).toFixed(1)}%`, ''
      ]),
      ['', '', '', ''],
      ['REZERVASYON ANALİZİ', '', '', ''],
      ['Ortalama Önceden Bildirim (Saat):', reportData.summary.avg_advance_booking_hours.toFixed(1), '', ''],
      ['', '', '', ''],
      ['GÜN DAĞILIMI', '', '', ''],
      ...Object.entries(reportData.summary.day_distribution).map(([day, count]) => [
        getDayName(day), count, `${((count / reportData.summary.total_transfers) * 100).toFixed(1)}%`, ''
      ]),
      ['', '', '', ''],
      ['SAAT DAĞILIMI', '', '', ''],
      ...Object.entries(reportData.summary.hour_distribution)
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([hour, count]) => [
          `${hour.padStart(2, '0')}:00`, count, `${((count / reportData.summary.total_transfers) * 100).toFixed(1)}%`, ''
        ]),
      ['', '', '', ''],
      ['EN ÇOK KULLANILAN ROTALAR', '', '', ''],
      ['Rota', 'Kullanım', 'Oran (%)', ''],
      ...reportData.summary.top_routes.slice(0, 10).map(([route, count]) => [
        route, count, `${((count / reportData.summary.total_transfers) * 100).toFixed(1)}%`, ''
      ])
    ];
    
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, '1-Özet Sayfası');
    
    // 2. ACENTEYE GÖRE RAPOR
    const agencyData = [
      ['ACENTEYE GÖRE RAPOR', '', '', '', ''],
      ['', '', '', '', ''],
      ['Acente Adı', 'Transfer Sayısı', 'Yolcu Sayısı', 'Ort. Yolcu/Transfer', 'Servis Tipleri'],
      ...Object.entries(reportData.by_agency).map(([agencyName, stats]) => [
        agencyName,
        stats.transfer_count,
        stats.passenger_count,
        (stats.passenger_count / stats.transfer_count).toFixed(1),
        Object.entries(stats.service_types).map(([type, count]) => `${getServiceTypeLabel(type)}: ${count}`).join(', ')
      ])
    ];
    
    const agencyWs = XLSX.utils.aoa_to_sheet(agencyData);
    XLSX.utils.book_append_sheet(wb, agencyWs, '2-Acenteye Göre');
    
    // 3. ŞOFÖRE GÖRE RAPOR
    const driverData = [
      ['ŞOFÖRE GÖRE RAPOR', '', '', '', ''],
      ['', '', '', '', ''],
      ['Şoför Adı', 'Transfer Sayısı', 'Yolcu Sayısı', 'Tamamlanan', 'Tamamlama Oranı (%)'],
      ...Object.entries(reportData.by_driver).map(([driverName, stats]) => [
        driverName,
        stats.transfer_count,
        stats.passenger_count,
        stats.completed_count,
        stats.completion_rate.toFixed(1)
      ])
    ];

    const driverWs = XLSX.utils.aoa_to_sheet(driverData);
    XLSX.utils.book_append_sheet(wb, driverWs, '3-Şoföre Göre');

    // 3.5. PLAKAYA GÖRE RAPOR
    const vehicleData = [
      ['PLAKAYA GÖRE RAPOR', '', '', '', ''],
      ['', '', '', '', ''],
      ['Plaka', 'Transfer Sayısı', 'Yolcu Sayısı', 'Tamamlanan', 'Tamamlama Oranı (%)'],
      ...Object.entries(reportData.by_vehicle || {}).map(([plate, stats]) => [
        plate,
        stats.transfer_count,
        stats.passenger_count,
        stats.completed_count,
        stats.completion_rate.toFixed(1)
      ])
    ];

    const vehicleWs = XLSX.utils.aoa_to_sheet(vehicleData);
    XLSX.utils.book_append_sheet(wb, vehicleWs, '4-Plakaya Göre');

    // Plaka bazında rota detayları (her plaka için ayrı satırlar)
    const vehicleRouteData = [
      ['PLAKA BAZINDA ROTA DETAYLARI', '', ''],
      ['', '', ''],
      ['Plaka', 'Rota', 'Sefer Sayısı']
    ];

    Object.entries(reportData.by_vehicle || {}).forEach(([plate, stats]) => {
      if (stats.top_routes && stats.top_routes.length > 0) {
        vehicleRouteData.push(['', '', '']); // Boş satır
        vehicleRouteData.push([plate, '', '']); // Plaka başlığı
        stats.top_routes.forEach(([route, count]) => {
          vehicleRouteData.push(['', route, count]);
        });
      }
    });

    const vehicleRouteWs = XLSX.utils.aoa_to_sheet(vehicleRouteData);
    XLSX.utils.book_append_sheet(wb, vehicleRouteWs, '5-Plaka Rotaları');

    // 6. HAVALIMANI İŞLERİ DETAY
    const airportData = [
      ['HAVALIMANI İŞLERİ DETAY LİSTESİ', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],
      ['ID', 'Tarih', 'Saat', 'Alış Adresi', 'Bırakış Adresi', 'Yolcu Adı', 'Yolcu Sayısı', 'Acente', 'Şoför', 'Durum'],
      ...reportData.airport_details.map(detail => [
        detail.id,
        detail.date,
        detail.time,
        detail.pickup,
        detail.dropoff,
        detail.passenger_name || '-',
        detail.pax_count,
        detail.agency,
        detail.driver || '-',
        getStatusLabel(detail.status)
      ])
    ];
    
    const airportWs = XLSX.utils.aoa_to_sheet(airportData);
    XLSX.utils.book_append_sheet(wb, airportWs, '6-Havalimanı İşleri');
    
    const fileName = `BHT_Aylik_Rapor_${reportData.period.year}_${String(reportData.period.month).padStart(2, '0')}_${reportData.period.month_name}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const getDayName = (englishDay) => {
    const dayNames = {
      'Monday': 'Pazartesi',
      'Tuesday': 'Salı', 
      'Wednesday': 'Çarşamba',
      'Thursday': 'Perşembe',
      'Friday': 'Cuma',
      'Saturday': 'Cumartesi',
      'Sunday': 'Pazar'
    };
    return dayNames[englishDay] || englishDay;
  };

  const getMonthOptions = () => {
    const months = [];
    for (let i = 1; i <= 12; i++) {
      const monthName = new Date(2023, i - 1).toLocaleDateString('tr-TR', { month: 'long' });
      months.push({ value: i, label: monthName });
    }
    return months;
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 4; i--) {
      years.push(i);
    }
    return years;
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      agencyId: '',
      pickupLocation: '',
      dropoffLocation: '',
      serviceType: '',
      status: '',
      driverName: ''
    });
    setPage(1);
  };

  const uniqueServiceTypes = [...new Set(transfers.map(t => t.service_type).filter(Boolean))];
  const uniqueStatuses = [...new Set(transfers.map(t => t.status).filter(Boolean))];
  const uniqueDrivers = [...new Set(transfers.map(t => t.driver_name).filter(Boolean))];

  // Arşiv sekmesi içeriği
  const renderArsivTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowMonthlyReportModal(true)}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Aylık Rapor
          </button>
          
          <button 
            onClick={exportToExcel}
            disabled={filteredTransfers.length === 0}
            className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel'e Aktar
          </button>
        </div>
        </div>

        {/* Filtreler */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtreleme Seçenekleri</h3>
        
        <div className="grid grid-cols-8 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Acente</label>
            <select
              value={filters.agencyId}
              onChange={(e) => handleFilterChange('agencyId', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Tümü</option>
              {agencies.map(agency => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          </div>
            <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Başlangıç</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bitiş</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Hizmet Tipi</label>
            <select
              value={filters.serviceType}
              onChange={(e) => handleFilterChange('serviceType', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Tümü</option>
              {uniqueServiceTypes.map(type => (
                <option key={type} value={type}>
                  {getServiceTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Alış Adresi</label>
            <input
              type="text"
              placeholder="Alış ara..."
              value={filters.pickupLocation}
              onChange={(e) => handleFilterChange('pickupLocation', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bırakış Adresi</label>
            <input
              type="text"
              placeholder="Bırakış ara..."
              value={filters.dropoffLocation}
              onChange={(e) => handleFilterChange('dropoffLocation', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Durum</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Tümü</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Şoför</label>
              <select
              value={filters.driverName}
              onChange={(e) => handleFilterChange('driverName', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Tümü</option>
              {uniqueDrivers.map(driver => (
                <option key={driver} value={driver}>
                  {driver}
                  </option>
                ))}
              </select>
            </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span>
                {Object.values(filters).filter(Boolean).length} filtre aktif • 
                {filteredTransfers.length} kayıt gösteriliyor
              </span>
            )}
          </div>
              <button
                onClick={clearFilters}
            disabled={Object.values(filters).filter(Boolean).length === 0}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
            Tüm Filtreleri Temizle
              </button>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acente</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alış</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bırakış</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih-Saat</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yolcu</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yolcu Adı</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TC/Kimlik</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hizmet</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Şoför</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notlar</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                      <span className="ml-2 text-gray-600">Yükleniyor...</span>
                    </div>
                  </td>
                </tr>
              )}
              
              {!loading && pageItems.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="font-medium">Kayıt bulunamadı</p>
                    <p className="text-sm">Filtreleri değiştirmeyi deneyin</p>
                  </td>
                </tr>
              )}
              
              {!loading && pageItems.map((transfer) => (
                <tr key={transfer.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{transfer.id}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-32 truncate" title={transfer.agency_name}>
                      {transfer.agency_name || 'BHT Clinic Admin'}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    <div className="max-w-40 truncate" title={transfer.pickup_addr}>
                      {transfer.pickup_addr}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    <div className="max-w-40 truncate" title={transfer.dropoff_addr}>
                      {transfer.dropoff_addr}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTime(transfer.pickup_date, transfer.pickup_time)}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {transfer.pax_count}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-32 truncate" title={transfer.passenger_name}>
                      {transfer.passenger_name || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-24 truncate" title={transfer.passenger_id_no}>
                      {transfer.passenger_id_no || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {getServiceTypeLabel(transfer.service_type)}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transfer.status)}`}>
                      {getStatusLabel(transfer.status)}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-32 truncate" title={transfer.driver_name}>
                      {transfer.driver_name || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    <div className="max-w-40 truncate" title={transfer.notes}>
                      {transfer.notes || '-'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center text-sm text-gray-600">
            <span>
              Toplam <span className="font-medium">{filteredTransfers.length}</span> kayıt • 
              Sayfa <span className="font-medium">{page}</span> / <span className="font-medium">{totalPages}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className={`px-3 py-1 rounded border text-sm font-medium transition-colors ${
                page <= 1 
                  ? 'text-gray-400 border-gray-200 cursor-not-allowed' 
                  : 'text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              Önceki
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className={`px-3 py-1 rounded border text-sm font-medium transition-colors ${
                page >= totalPages 
                  ? 'text-gray-400 border-gray-200 cursor-not-allowed' 
                  : 'text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              Sonraki
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Rapor sekmesi içeriği
  const renderRaporTab = () => (
    <ReportAnalytics 
      agencies={agencies}
      getServiceTypeLabel={getServiceTypeLabel}
      getDayName={getDayName}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mb-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Rapor & Arşiv</h1>
          <p className="text-gray-600">Transfer kayıtları ve raporlama sistemi</p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('arsiv')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'arsiv'
                  ? 'border-bht-red text-bht-red'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Arşiv
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('rapor')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'rapor'
                  ? 'border-bht-red text-bht-red'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Rapor
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'arsiv' && renderArsivTab()}
        {activeTab === 'rapor' && renderRaporTab()}
      </div>

      {/* Aylık Rapor Modal */}
      {showMonthlyReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Aylık Rapor Oluştur</h3>
              <button
                onClick={() => setShowMonthlyReportModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yıl</label>
                  <select
                    value={monthlyReportData.year}
                    onChange={(e) => setMonthlyReportData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    {getYearOptions().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ay</label>
                  <select
                    value={monthlyReportData.month}
                    onChange={(e) => setMonthlyReportData(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    {getMonthOptions().map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Acente (İsteğe Bağlı)</label>
                  <select
                    value={monthlyReportData.agencyId}
                    onChange={(e) => setMonthlyReportData(prev => ({ ...prev, agencyId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tüm Acenteler</option>
                    {agencies.map(agency => (
                      <option key={agency.id} value={agency.id}>
                        {agency.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-blue-900">Aylık Rapor İçeriği</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Seçilen dönem için transfer istatistikleri, acente performansı ve detaylı analiz raporunu içerecektir.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowMonthlyReportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={generateMonthlyReport}
                disabled={monthlyReportLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
              >
                {monthlyReportLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {monthlyReportLoading ? 'Oluşturuluyor...' : 'Rapor Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaporArsiv;